import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Signup.scss';
import { incrementSignupStep, resetSignup, selectSignup, selectSignupEmail, selectStep } from './signupSlice';
import { RegistrationErrorHandler } from '../../components/registration/RegistrationErrorHandler';
import { HmiType } from '../hmi/hmi.interface';
import { SignupParamsHandler } from './SignupParamsHandler';
import { SignupEmail } from './SignupEmail';
import { AnalyticsContext, HmiV2 } from 'hmi';
import { CreatingPDA } from './CreatingPDA';
import { SignupApplicationHandler } from './SignupApplicationHandler';
import { IDaasSignup } from '../../services/DaasSignupService';
import { getMobileOperatingSystem, getParameterByName } from '../../utils/WindowHelper';
import { signupError } from '../../utils/ErrorHandling';
import {
  getApplicationDependency,
  getToolDependency,
  selectDependencyApps,
  selectDependencyTools,
  selectParentApp,
} from '../hmi/hmiSlice';
import { selectLanguage } from '../language/languageSlice';
import SignupBackButton from './SignupBackButton';
import { ConfirmYourIdentity } from '../loginEmail/ConfirmYourIdentity';
import { setRedirectError } from '../redirectError/redirectErrorSlice';
import { AnalyticsClickEvents } from '../../utils/AnalyticsEvents';
import { createPdaAuthUser } from '../../api/hattersAPI';

type Props = {
  path?: string;
};

export const Signup: React.FC<Props> = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onClickEvent = useContext(AnalyticsContext)?.onClickEvent;
  const [signupDone, setSignupDone] = useState(false);
  const [nextStep, setNextStep] = useState<string | undefined>('');
  const dependencyApps = useSelector(selectDependencyApps);
  const dependencyTools = useSelector(selectDependencyTools);

  const dispatch = useDispatch();
  const parentApp = useSelector(selectParentApp);
  const email = useSelector(selectSignupEmail);
  const signup = useSelector(selectSignup);
  const step = useSelector(selectStep);
  const language = useSelector(selectLanguage);

  const handleSignup = async (signupPayload: IDaasSignup, lang?: string | null, skipDeps?: string | null) => {
    const signupStartTime = performance.now();
    signupPayload.platform = getMobileOperatingSystem();

    try {
      const res = await createPdaAuthUser(signupPayload, lang, skipDeps);
      if (res.parsedBody) {
        const signupEndTime = performance.now();
        const analyticsEvent = Object.assign({}, AnalyticsClickEvents.registrationTimePerformance);

        analyticsEvent.value = Math.round(signupEndTime - signupStartTime);
        onClickEvent?.(analyticsEvent);

        setSignupDone(true);
        setNextStep(res.parsedBody.nextStep);
      }
    } catch (error) {
      const signupEndTime = performance.now();
      const analyticsEvent = Object.assign({}, AnalyticsClickEvents.registrationErrorTimePerformance);

      analyticsEvent.value = Math.round(signupEndTime - signupStartTime);
      onClickEvent?.(analyticsEvent);

      signupError(error?.value || 0);
    }
  };

  const setUserNotVerified = (email: string, nextStep: string) => {
    setNextStep(nextStep);
    dispatch(incrementSignupStep(3));
  };

  useEffect(() => {
    if (step === 2 && !isSubmitting) {
      const lang = getParameterByName('lang');
      const skipDeps = getParameterByName('skip_deps');

      handleSignup(Object.assign({}, signup), lang, skipDeps);
      setIsSubmitting(true);
    }
    // eslint-disable-next-line
  }, [step]);

  useEffect(() => {
    return () => {
      dispatch(resetSignup());
    };
  }, [dispatch]);

  useEffect(() => {
    if (parentApp && parentApp.dependencies) {
      if (parentApp.dependencies.plugs.length > 0) {
        parentApp.dependencies.plugs.forEach((appId) => {
          dispatch(getApplicationDependency(appId));
        });
      }

      if (parentApp.dependencies.tools.length > 0) {
        dispatch(getToolDependency(parentApp.dependencies.tools[0]));
      }
    }
    // eslint-disable-next-line
  }, [parentApp]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    // This is a fix to adjust a white background colour for this service to body.
    document.body.style.backgroundColor = 'white';
  }, []);

  const isHatApp = ['hatapp', 'hatappstaging'].indexOf(parentApp?.id || '') !== -1;

  return (
    <RegistrationErrorHandler>
      <SignupParamsHandler>
        <SignupApplicationHandler>
          <SignupBackButton />
          {step === 0 && <SignupEmail setUserNotVerified={(email, nextStep) => setUserNotVerified(email, nextStep)} />}

          {step === 1 && parentApp && (
            <HmiV2
              email={email}
              parentApp={parentApp}
              onApproved={() => dispatch(incrementSignupStep(2))}
              onRejected={() => {
                dispatch(
                  setRedirectError({
                    error: 'user_cancelled',
                    reason: 'permissions_denied',
                  }),
                );
              }}
              language={language}
              dependencyTools={dependencyTools.map((tool) => tool.info.name)}
              dependencyApps={dependencyApps}
              applicationId={signup.applicationId}
              hmiType={HmiType.daas}
            />
          )}

          {step === 2 && <CreatingPDA done={signupDone} nextStep={nextStep} email={email} isHatApp={isHatApp} />}
          {step === 3 && <ConfirmYourIdentity email={email} />}
        </SignupApplicationHandler>
      </SignupParamsHandler>
    </RegistrationErrorHandler>
  );
};
